import { z } from 'zod';

/** DATA_SCHEMA v0.3 Amendment로 확정된 스키마 버전. 실제 자료 투입 후에는 migration으로만 증가한다. */
export const SCHEMA_VERSION = 1 as const;

/**
 * 하위 호환 확장(선택 필드·enum 값 추가) 차수. 기존 기록은 그대로 유효해 데이터 변환은 없지만,
 * 이전 앱은 새 값을 읽지 못하므로 동기화에서 이 값이 더 큰 기기의 자료는 받지 않는다 (앱 업데이트 안내).
 * A21=1, A23=2, A24=3, A25=4, A26=5, A28=6, A29=7, A30=8, A33=9, A35=10, A36=11
 */
export const DATA_FORMAT_REVISION = 12 as const;

export const ENTITY_TYPES = [
  'Client',
  'ClientIdentity',
  'IntakeSummary',
  'Session',
  'Transcript',
  'TranscriptDiscrepancy',
  'CounselorMemo',
  'ClinicalEvidence',
  'SessionAnalysis',
  'RiskAssessment',
  'CounselingGoal',
  'GoalProgress',
  'CaseFormulation',
  'CaseFormulationRevision',
  'DiagnosticConsideration',
  'CounselingJournal',
  'NextSessionBrief',
  'SupervisionNote',
  'DocumentArtifact',
  'FileReference',
  'AIImport',
  'AuditEvent',
  'SyncState',
  'SyncConflict',
  'PsychologicalAssessment',
  'VerbatimSelection',
  'CounselorReflection',
  'AppSettings',
  'GlossaryTerm',
] as const;
export const EntityTypeSchema = z.enum(ENTITY_TYPES);
export type EntityType = z.infer<typeof EntityTypeSchema>;

/** UUIDv7 (RFC 9562) */
export const IdSchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/, 'UUIDv7 형식이어야 합니다');

/** ISO-8601 UTC 타임스탬프 */
export const IsoDateTimeSchema = z.string().datetime({ offset: false, precision: 3 });
/** 달력 날짜 (YYYY-MM-DD) */
export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const ActorSchema = z.enum(['user', 'system', 'ai-import']);
export type Actor = z.infer<typeof ActorSchema>;

export const Confidence = z.number().min(0).max(1);

/** Amendment A33: 치료 접근 (workflow/approaches의 목록과 같은 순서) */
export const APPROACH_IDS = ['cbt', 'eft', 'act', 'pct', 'pdt', 'sfbt', 'narr', 'fam', 'att', 'past'] as const;
export const ApproachIdSchema = z.enum(APPROACH_IDS);
export type ApproachId = z.infer<typeof ApproachIdSchema>;
export const ApproachEvidenceKindSchema = z.enum(['need', 'fit', 'indication', 'applied']);
export type ApproachEvidenceKind = z.infer<typeof ApproachEvidenceKindSchema>;

const baseShape = {
  id: IdSchema,
  schemaVersion: z.literal(SCHEMA_VERSION),
  revision: z.number().int().min(1),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  createdBy: ActorSchema,
  updatedBy: ActorSchema,
  lastModifiedDevice: z.string().min(1),
  deletedAt: IsoDateTimeSchema.nullable().optional(),
};

/** 모든 핵심 객체의 공통 메타 (Amendment A0). entityType은 literal로 고정한다. */
export function entity<T extends EntityType, S extends z.ZodRawShape>(entityType: T, shape: S) {
  return z.object({ ...baseShape, entityType: z.literal(entityType), ...shape }).strict();
}

export type BaseEntity = {
  id: string;
  entityType: EntityType;
  schemaVersion: number;
  revision: number;
  createdAt: string;
  updatedAt: string;
  createdBy: Actor;
  updatedBy: Actor;
  lastModifiedDevice: string;
  deletedAt?: string | null;
};
